import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Plug,
  ShoppingCart,
  Wallet,
  Landmark,
  Building2,
  LogOut,
  ShieldCheck,
  Clock,
  HandCoins,
  BarChart3,
  Scale,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/integrations", label: "Integraciones", icon: Plug, testid: "nav-integrations" },
  { to: "/orders", label: "Órdenes", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/receivables", label: "Cuentas por cobrar", icon: Clock, testid: "nav-receivables" },
  { to: "/reconciliation", label: "Conciliación", icon: Scale, testid: "nav-reconciliation" },
  { to: "/cash", label: "Caja", icon: Wallet, testid: "nav-cash" },
  { to: "/debts", label: "Deudas", icon: Landmark, testid: "nav-debts" },
  { to: "/advances", label: "Anticipos", icon: HandCoins, testid: "nav-advances" },
  { to: "/reports", label: "Reportes", icon: BarChart3, testid: "nav-reports" },
  { to: "/organization", label: "Negocio", icon: Building2, testid: "nav-organization" },
];

const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-black text-white" data-testid="sidebar">
        <div className="flex h-16 items-center gap-3 border-b border-white/10 px-6">
          <img src="/silik-icono.png" alt="Silik Prötek" className="h-8 w-8 object-contain" />
          <div className="leading-tight">
            <div className="font-black tracking-tight" style={{ fontFamily: "Bodoni Moda" }}>
              Silik Prötek
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Control de Caja</div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-5">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#DD0000] text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/10 px-6 py-4 text-[11px] text-slate-400">
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#FFCC00]" />
            Moneda única · MXN
          </div>
        </div>
      </aside>

      {/* Topbar */}
      <header className="fixed inset-x-0 left-64 top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-8 backdrop-blur-md">
        <div className="text-sm text-slate-500">
          Sistema de control financiero-operativo <span className="text-slate-300">·</span>{" "}
          <span className="font-medium text-slate-700">1 negocio</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right leading-tight">
            <div className="text-sm font-medium text-slate-800" data-testid="topbar-user-name">
              {user?.name}
            </div>
            <div className="text-[11px] text-slate-400">{user?.email}</div>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
            {user?.name?.slice(0, 1) || "A"}
          </div>
          <button
            onClick={() => logout()}
            data-testid="logout-button"
            className="flex items-center gap-2 rounded-sm border border-slate-200 px-3 py-2 text-sm text-slate-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-600"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Salir
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="ml-64 min-h-screen px-8 pb-16 pt-24">{children}</main>
    </div>
  );
};

export default AppShell;
